// OPFS (Origin Private File System) content-addressed cache (Local Engine Phase 3).
// Stores proxies / rendered segments / thumbnails / export chunks locally, keyed by
// content hash, so unchanged assets are reused across sessions. Degrades gracefully
// to a no-op (available:false) when OPFS is unavailable.

const DIR = "captionmotion-cache";

function supported() {
  return typeof navigator !== "undefined" && navigator.storage && typeof navigator.storage.getDirectory === "function";
}

async function dir() {
  const root = await navigator.storage.getDirectory();
  return root.getDirectoryHandle(DIR, { create: true });
}

export async function sha256Hex(buf) {
  const data = buf instanceof ArrayBuffer ? buf : await buf.arrayBuffer();
  const digest = await crypto.subtle.digest("SHA-256", data);
  return [...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, "0")).join("");
}

export const opfsCache = {
  available: supported(),

  async put(key, blob) {
    if (!supported()) return false;
    const d = await dir();
    const fh = await d.getFileHandle(key, { create: true });
    const w = await fh.createWritable();
    await w.write(blob);
    await w.close();
    return true;
  },

  async get(key) {
    if (!supported()) return null;
    try {
      const d = await dir();
      const fh = await d.getFileHandle(key, { create: false });
      return await fh.getFile();
    } catch { return null; }
  },

  async has(key) {
    if (!supported()) return false;
    try { const d = await dir(); await d.getFileHandle(key, { create: false }); return true; } catch { return false; }
  },

  async delete(key) {
    if (!supported()) return false;
    try { const d = await dir(); await d.removeEntry(key); return true; } catch { return false; }
  },

  // Total cached bytes + entry count (best-effort).
  async stats() {
    if (!supported()) return { available: false, entries: 0, bytes: 0 };
    let entries = 0, bytes = 0;
    try {
      const d = await dir();
      for await (const [, handle] of d.entries()) {
        if (handle.kind === "file") { const f = await handle.getFile(); entries++; bytes += f.size; }
      }
    } catch { /* ignore */ }
    return { available: true, entries, bytes };
  },

  // Evict oldest entries beyond a byte budget (simple LRU via lastModified).
  async prune(maxBytes = 512 * 1024 * 1024) {
    if (!supported()) return;
    try {
      const d = await dir();
      const files = [];
      for await (const [name, handle] of d.entries()) {
        if (handle.kind === "file") { const f = await handle.getFile(); files.push({ name, size: f.size, mtime: f.lastModified }); }
      }
      let total = files.reduce((s, f) => s + f.size, 0);
      files.sort((a, b) => a.mtime - b.mtime);
      for (const f of files) {
        if (total <= maxBytes) break;
        await d.removeEntry(f.name); total -= f.size;
      }
    } catch { /* ignore */ }
  },
};
